import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const navLinks = [{
  name: 'Home',
  href: '/'
}, {
  name: 'About Us',
  href: '/about'
}, {
  name: 'Services',
  href: '/#services',
  dropdown: [{
    name: 'Personal Injury',
    href: '/services/personal-injury'
  }, {
    name: 'Clinical Negligence',
    href: '/services/clinical-negligence'
  }, {
    name: 'Family Court',
    href: '/services/family-court'
  }, {
    name: 'Criminal Cases',
    href: '/services/criminal-cases'
  }, {
    name: 'Immigration',
    href: '/services/immigration'
  }, {
    name: 'Industrial Diseases',
    href: '/services/industrial-disease'
  }]
}, {
  name: 'Panel Experts',
  href: '/panel-experts'
}, {
  name: 'Policies',
  href: '/privacy-policy',
  dropdown: [{
    name: 'Privacy & Cookie Policy',
    href: '/privacy-policy'
  }, {
    name: 'Terms & Conditions',
    href: '/terms-conditions'
  }, {
    name: 'Cancellation Policy',
    href: '/cancellation-policy'
  }]
}, {
  name: 'FAQ',
  href: '/faq'
}, {
  name: 'Contact',
  href: '/contact'
}];
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Handle smooth scrolling to sections
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const sectionId = href.substring(2); // Remove '/#'
      // If we're not on the home page, navigate there first
      if (window.location.pathname !== '/') {
        window.location.href = href;
        return;
      }
      // Smooth scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 100; // Account for fixed header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
      setIsOpen(false);
    }
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm py-3 border-b border-warm-200' : 'bg-white py-5 border-b border-warm-200'}`} data-id="element-326">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-327">
        <div className="flex justify-between items-center" data-id="element-328">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 group" data-id="element-329">
            <img src="https://cdn.magicpatterns.com/uploads/cfHMrrPng3iagpRwV1FuNs/Favicon.jpg" alt="UK Medicolegal Assessments shield logo" className="h-10 w-10 rounded-lg object-contain" data-id="element-330" />
            <span className="text-xl font-bold tracking-tight text-ink-900 hidden sm:inline" data-id="element-331">
              UK<span className="text-teal-500" data-id="element-332"> MEDICOLEGAL</span> ASSESSMENTS
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-8" data-id="element-333">
            {navLinks.map(link => <div key={link.name} className="relative group" data-id="element-334">
                {link.dropdown ? <>
                    <button className="text-sm font-semibold text-ink-500 hover:text-teal-600 transition-colors duration-200 flex items-center" onMouseEnter={() => setActiveDropdown(link.name)} data-id="element-335">
                      {link.name}
                      <ChevronDown className="ml-1 h-4 w-4" data-id="element-336" />
                    </button>
                    {activeDropdown === link.name && <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-warm-200 py-2 overflow-hidden" onMouseLeave={() => setActiveDropdown(null)} data-id="element-337">
                        {link.dropdown.map(item => <a key={item.name} href={item.href} onClick={e => handleNavClick(e, item.href)} className="block px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-warm-50 hover:text-teal-600 transition-colors duration-200" data-id="element-338">
                            {item.name}
                          </a>)}
                      </div>}
                  </> : <a href={link.href} onClick={e => handleNavClick(e, link.href)} className="text-sm font-semibold text-ink-500 hover:text-teal-600 transition-colors duration-200" data-id="element-339">
                    {link.name}
                  </a>}
              </div>)}
            <a href="/contact" className="btn-primary py-2.5 px-6 min-h-0" data-id="element-340">
              Instruct Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden" data-id="element-341">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-lg text-ink-900 hover:bg-warm-50 transition-colors" aria-label="Toggle menu" data-id="element-342">
              {isOpen ? <X className="h-6 w-6" data-id="element-343" /> : <Menu className="h-6 w-6" data-id="element-344" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-345">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="lg:hidden bg-white border-t border-warm-200 overflow-hidden" data-id="element-346">
            <div className="px-5 py-6 space-y-4" data-id="element-347">
              {navLinks.map(link => <div key={link.name} data-id="element-348">
                  {link.dropdown ? <>
                      <div className="px-3 py-2 text-base font-semibold text-ink-900" data-id="element-349">
                        {link.name}
                      </div>
                      <div className="pl-6 space-y-2 mt-1" data-id="element-350">
                        {link.dropdown.map(item => <a key={item.name} href={item.href} onClick={e => {
                  handleNavClick(e, item.href);
                  setIsOpen(false);
                }} className="block px-3 py-2 text-sm font-medium text-ink-500 hover:text-teal-600 hover:bg-warm-50 rounded-lg transition-colors" data-id="element-351">
                            {item.name}
                          </a>)}
                      </div>
                    </> : <a href={link.href} onClick={e => {
              handleNavClick(e, link.href);
              setIsOpen(false);
            }} className="block px-3 py-2 text-base font-semibold text-ink-700 hover:text-teal-600 hover:bg-warm-50 rounded-lg transition-colors" data-id="element-352">
                      {link.name}
                    </a>}
                </div>)}
              <div className="pt-6 border-t border-warm-100" data-id="element-353">
                <a href="/contact" onClick={() => setIsOpen(false)} className="btn-primary w-full" data-id="element-354">
                  Instruct Us
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}